import React, { useState, useEffect } from "react";
import { useAuth } from "../services/AuthContext";
import { getUserProfile, updateUserProfile } from "../services/Api";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";

import ProfileMenu from "./ProfileMenu";
import ProfileContent from "./ProfileContent";
import CguModal from "./CguModal";
import PrivacyModal from "./PrivacyModal";
import CookiePolicyModal from "./CookiePolicyModal";

import "../css/Profile.css";

const Profile = () => {
  const { token, isUserAuthenticated, authLoading, handleLogout, updateGender } = useAuth();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeSection, setActiveSection] = useState(location.state?.section || "profile");
  const [profileData, setProfileData] = useState({});
  const [errors, setErrors] = useState({});
  const [showCgu, setShowCgu] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showCookiePolicy, setShowCookiePolicy] = useState(false);

  const formatDate = (isoString) => isoString.split("T")[0];

  useEffect(() => {

    if (authLoading) return;

    if (!isUserAuthenticated) {
      navigate("/");
      return;
    }

    const fetchProfile = async () => {
      try {
        const data = await getUserProfile(token);
        // Resynchronise le genre du contexte avec le profil serveur
        updateGender(data.gender);
        setProfileData({
          ...data,
          dateOfBirth: data.dateOfBirth ? formatDate(data.dateOfBirth) : "",
        });
      } catch (error) {
        console.error("Erreur lors de la récupération du profil:", error.message);
      }
    };

    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isUserAuthenticated, authLoading, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData({ ...profileData, [name]: value });
  };

  const handleSubmit = async (validatedData) => {
    try {
      const profileToSave = {
        ...validatedData,
        dateOfBirth: validatedData.dateOfBirth
          ? new Date(validatedData.dateOfBirth).toISOString()
          : "",
      };

      const updatedProfile = await updateUserProfile(profileToSave);
      // Le genre a pu changer : on met à jour le contexte pour les libellés genrés
      updateGender(updatedProfile.gender);
      setProfileData({
        ...updatedProfile,
        dateOfBirth: updatedProfile.dateOfBirth
          ? formatDate(updatedProfile.dateOfBirth)
          : "",
      });
    } catch (error) {
      if (error.response && error.response.data && error.response.data.errors) {
        //console.log("✅ Erreurs reçues :", error.response.data.errors);
        setErrors(error.response.data.errors);
      } else {
        console.warn("Aucun objet errors reçu du backend");
        setErrors({ general: "Une erreur inattendue est survenue." });
      }
    }
  };

  return (
    <>
      <div className="profile-container">
        {/* Menu horizontal */}
        <ProfileMenu 
          activeSection={activeSection} 
          setActiveSection={setActiveSection}
          t={t} 
        />

        {/* Zone de contenu dynamique */}
        <div style={{ flex: 1 }}>
          <ProfileContent
            activeSection={activeSection}
            profileData={profileData}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            errors={errors}
            setErrors={setErrors}
            i18n={i18n}
            t={t}
            handleLogout={handleLogout}
          />
        </div>
      </div>

      <div className="profile-legal-footer">
        <span className="profile-legal-link" onClick={() => setShowCgu(true)}>
          {t("profile.cgu", "Conditions générales d'utilisation")}
        </span>
        <span className="profile-legal-separator">•</span>
        <span className="profile-legal-link" onClick={() => setShowPrivacy(true)}>          
          {t("profile.privacy", "Politique de confidentialité")}
        </span>
        <span className="profile-legal-separator">•</span>
        <span className="profile-legal-link" onClick={() => setShowCookiePolicy(true)}>
          {t("profile.cookies", "Politique des cookies")}
        </span>
      </div>

      <CguModal
        show={showCgu}
        onRefuse={() => setShowCgu(false)}
        readOnly={true}
      />

      <PrivacyModal
        show={showPrivacy}
        onRefuse={() => setShowPrivacy(false)}
        readOnly={true}
      />

      <CookiePolicyModal
        show={showCookiePolicy}
        onHide={() => setShowCookiePolicy(false)}
      />
    </>
  );
};

export default Profile;